import { useEffect, useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAuthStore } from '@/features/auth/store'
import { queryKeys } from '@/lib/queryKeys'
import { getOnboarding, submitOnboarding } from './api'
import { isSignupSession } from './signupSession'
import { getSubscription, isSubscriptionPaidActive } from '@/features/subscriptions/api'
import { TrialBriefPage } from './TrialBriefPage'

const FIVE_MINUTES_MS = 5 * 60 * 1000

/**
 * 5 min après l'inscription si la personne est encore dans cette session.
 * Sinon, dès la reconnexion. Jamais si le back a déjà un answeredAt.
 */
export function TrialBriefGate({ disabled = false }: { disabled?: boolean }) {
  const user = useAuthStore((s) => s.user)
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const { data, isLoading, isError } = useQuery({
    queryKey: queryKeys.onboarding,
    queryFn: getOnboarding,
    enabled: Boolean(user) && !disabled,
    retry: false,
  })
  const { data: plan, isLoading: planLoading, isError: planError, isFetched: planFetched } = useQuery({
    queryKey: queryKeys.subscription,
    queryFn: getSubscription,
    enabled: Boolean(user) && !disabled,
  })
  const paidActive = isSubscriptionPaidActive(plan)

  useEffect(() => {
    if (disabled || !user?.createdAt || isLoading || isError || data?.answeredAt) return
    if (!planFetched || planLoading || planError || paidActive) return
    const dueAt = new Date(user.createdAt).getTime() + FIVE_MINUTES_MS
    const wait = isSignupSession() ? Math.max(0, dueAt - Date.now()) : 0
    const id = window.setTimeout(() => setOpen(true), wait)
    return () => window.clearTimeout(id)
  }, [disabled, user?.createdAt, isLoading, isError, data?.answeredAt, planFetched, planLoading, planError, paidActive])

  if (disabled) return null

  return (
    <TrialBriefPage
      embedded
      open={open}
      onOpenChange={setOpen}
      onAnswered={async (answers) => {
        const { record, created } = await submitOnboarding(answers)
        queryClient.setQueryData(queryKeys.onboarding, record)
        if (created) {
          await queryClient.invalidateQueries({ queryKey: queryKeys.subscription })
        }
        setOpen(false)
      }}
    />
  )
}
